<template>
  <Editor
    class="tag-editor"
    :titleI18nKey="`admin.crm.title.tag.${isEditMode ? 'edit' : 'add'}`"
    :model="tag"
    :loading.sync="loading"
    :formLines="formLines"
    @cancel="handleCancel"
    @save="handleSave">
    <template #conditionRule="{ dataKey }">
      <el-radio-group v-model="tag[dataKey]">
        <el-radio :label="ruleOptions.all">{{ $t(`admin.crm.option.tag.${ruleOptions.all}`) }}</el-radio>
        <el-radio :label="ruleOptions.any">{{ $t(`admin.crm.option.tag.${ruleOptions.any}`) }}</el-radio>
        <el-radio :label="ruleOptions.no">{{ $t(`admin.crm.option.tag.${ruleOptions.no}`) }}</el-radio>
      </el-radio-group>
    </template>
    <template #conditions>
      <div class="condition-container">
        <div class="add-condition-button" @click="handleAddConditionBtnClick">{{ $t('admin.crm.btn.tag.addCondition') }}</div>
        <div class="condition-list">
          <el-collapse v-if="tag.conditions.length > 0" v-model="activeConditionItems" @change="handleCollapseChange">
            <el-collapse-item
              v-for="(item, index) in tag.conditions"
              class="condition-item"
              :key="index"
              :name="item._vid">
              <template #title>
                <div class="condition-item-title-wrapper">
                  <div class="condition-item-title">
                    <div class="item-name">
                      {{ $t('admin.crm.label.tag.conditionNo', { number: index + 1})}}
                    </div>
                    <div v-if="item.showTitle" class="item-preview">
                      <template v-if="item.relationType === conditionRelationType.CONTAINS">
                        <span> {{ $t(`admin.crm.condition.name.${item.name}`) }} </span>
                        <span> {{  $t(`admin.crm.condition.relation.${item.relation}`) }} </span>
                        <span> {{ generateConditionDisplayValue(item) }} </span>
                      </template>
                      <template v-else>
                        <span> {{ $t(`admin.crm.condition.name.${item.name}`) }} </span>
                        <span> {{ generateConditionDisplayValue(item) }} </span>
                        <span> {{  $t(`admin.crm.condition.relation.${item.relation}`) }} </span>
                      </template>
                    </div>
                  </div>
                  <div class="condition-operate">
                    <div class="remove-btn">
                      <i class="el-icon-close" @click="removeConditionItem(index)"></i>
                    </div>
                  </div>
                </div>
              </template>
              <div class="condition-content">
                <div class="condition-choice-container">
                  <div class="condition-content-item">
                    <el-select v-model="item.name" @change="handleConditionChange(item)">
                      <el-option
                        v-for="(nameItem, nameKey) in conditionOptions"
                        :key="nameKey"
                        :label="$t(nameItem.i18nKey)"
                        :value="nameItem.value"/>
                    </el-select>
                  </div>
                  <div class="contains-container condition-content-item" v-if="item.relationType === conditionRelationType.CONTAINS">
                    <div class="condition-content-item relation">
                      <el-radio-group v-model="item.relation">
                        <el-radio v-if="showEqualsCondition(item)" :label="conditionRelation.EQUALS">{{ $t('admin.crm.condition.relation.equals') }}</el-radio>
                        <el-radio v-if="showContainsCondition(item)" :label="conditionRelation.CONTAINS">{{ $t('admin.crm.condition.relation.contains') }}</el-radio>
                      </el-radio-group>
                    </div>
                    <div :class="item.showKeywordError ? 'keyword-error' : ''" class="condition-value-container">
                      <template v-if="item.name !== 'gender'">
                        <div v-if="item.iptValueList.length > 0" class="condition-value-list" >
                          <el-tag
                            v-for="(conditionValueItem, index) in item.iptValueList"
                            :key="index"
                            closable
                            :disable-transitions="true"
                            @close="removeConditionValueItem(item, conditionValueItem)">
                            {{ conditionValueItem }}
                          </el-tag>
                        </div>
                        <div class="condition-value-item-input">
                          <el-input v-model="item.iptValue" :placeholder="$t('admin.crm.placeholder.tag.condition.relation.stringValue')" @keyup.enter.native="handleInputEnter(item)" @blur="handleInputEnter(item)" @focus="clearKeywordError(item)"/>
                        </div>
                      </template>
                      <template v-else>
                        <div class="condition-value-item-input">
                          <el-select v-model="item.iptValueList[0]" :placeholder="$t('admin.crm.placeholder.tag.condition.relation.gender')">
                            <el-option :label="$t('common.gender.female')" value="F"></el-option>
                            <el-option :label="$t('common.gender.male')" value="M"></el-option>
                            <el-option :label="$t('common.gender.nonBinary')" value="N"></el-option>
                            <el-option :label="$t('common.gender.preferNotToDisclose')" value="P"></el-option>
                          </el-select>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div class="range-container condition-content-item" v-if="item.relationType === conditionRelationType.RANGE" >
                    <div class="condition-content-item">
                      <el-radio-group v-model="item.relation" @change="handleRangeTypeChange(item)">
                        <el-radio :label="conditionRelation.MORE">{{ $t('admin.crm.condition.relation.more') }}</el-radio>
                        <el-radio :label="conditionRelation.BETWEEN">{{ $t('admin.crm.condition.relation.between') }}</el-radio>
                        <el-radio :label="conditionRelation.LESS">{{ $t('admin.crm.condition.relation.less') }}</el-radio>
                      </el-radio-group>
                    </div>
                    <div v-if="item.relation === conditionRelation.BETWEEN" class="range-between">
                        <div :class="item.showKeywordError && !item.iptValueMin ? 'keyword-error' : ''" >
                          <el-input
                            :placeholder="$t(`admin.crm.placeholder.tag.condition.relation.less_${item.name}`)"
                            v-model.number="item.iptValueMin" @focus="clearKeywordError(item)"/>
                        </div>
                      <div class="between-to">{{ $t('admin.crm.condition.relation.to') }}</div>
                        <div :class="item.showKeywordError && !item.iptValueMax ? 'keyword-error' : ''" >
                          <el-input
                          :placeholder="$t(`admin.crm.placeholder.tag.condition.relation.more_${item.name}`)"
                          v-model.number="item.iptValueMax" @focus="clearKeywordError(item)"/>
                        </div>
                    </div>
                    <div v-else>
                        <div :class="item.showKeywordError ? 'keyword-error' : ''" >
                          <el-input v-model.number="item.iptValueSingle" :placeholder="$t('admin.crm.placeholder.tag.condition.relation.intValue')" @focus="clearKeywordError(item)" />
                        </div>
                    </div>
                  </div>
                </div>
                <div class="keyword-error-message" v-if="item.showKeywordError">{{ $t('admin.crm.message.tag.keywordNotEmpty') }}</div>
                <div class="confirm-condition-button">
                  <el-button type="primary" @click="handleConfirmConditionBtnClick(item)">{{ $t('common.btn.confirm') }}</el-button>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
    </template>
  </Editor>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import {
  CONDITION_RULES,
  CONDITIONS,
  CONDITIONS_IN_RANGE,
  CONDITION_RELATION,
  CONDITIONS_RELATION_TYPE,
  CONDITIONS_IN_CONTAINS,
  CONDITIONS_IN_EQUALS
} from '@/constant/crm'
import Editor from '../editor.vue'

export default {
  components: {
    Editor
  },
  data () {
    return {
      tag: {
        condition_rule: CONDITION_RULES.all,
        conditions: []
      },
      loading: false,
      ruleOptions: CONDITION_RULES,
      conditionOptions: CONDITIONS,
      conditionRelation: CONDITION_RELATION,
      conditionRelationType: CONDITIONS_RELATION_TYPE,
      conditionVid: 1,
      isRangeRelation: false,
      activeConditionItems: []
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    formLines () {
      const vm = this
      const lines = {
        tagName: {
          dataKey: 'tag_name',
          labelI18nKey: 'admin.crm.label.tag.tagName',
          placeholderI18nKey: 'admin.crm.placeholder.tag.tagName',
          rules: [
            { required: true, message: this.$t('admin.crm.message.tag.tagName') },
            { validator: this.validatorTagName, trigger: 'change' }
          ]
        },
        tag_cn_name: {
          dataKey: 'tag_cn_name',
          labelI18nKey: 'admin.crm.label.tag.tagCnName',
          placeholderI18nKey: 'admin.crm.placeholder.tag.tagName',
          rules: [
            { required: true, message: this.$t('admin.crm.message.tag.tagName') },
            { validator: this.validatorTagName, trigger: 'change' }
          ]
        },
        conditionRule: {
          dataKey: 'condition_rule',
          labelI18nKey: 'admin.crm.label.tag.conditionRule',
          rules: [{ required: true, message: this.$t('admin.crm.message.tag.conditionRule') }]
        },
        conditions: {
          get hidden () { return vm.tag.condition_rule === CONDITION_RULES.no },
          labelI18nKey: 'admin.crm.label.tag.setCondition',
          rules: [{ required: this.tag.condition_rule !== this.ruleOptions.no, message: this.$t('admin.crm.message.tag.conditions') }]
        }
      }
      return lines
    },
    form () {
      return this.$refs.formEditor.form
    }
  },
  created () {
    if (this.isEditMode) {
      this.getTag()
    }
  },
  methods: {
    ...mapActions('crm', ['getTagById', 'createTag', 'updateTag']),
    async getTag () {
      this.loading = true
      try {
        const tagId = this.$route.params.tagId
        const resp = await this.getTagById(tagId)
        if (resp) {
          this.tag = {
            id: resp.id,
            tag_name: resp.name,
            tag_cn_name: resp.cn_name,
            condition_rule: resp.match_condition_rule,
            conditions: _.map(JSON.parse(resp.condition), item => {
              this.conditionVid += 1
              const condition = {
                _vid: this.conditionVid,
                name: item.name,
                iptValue: null, // it is used for user enter each keyword in the input box
                iptValueSingle: null, // iptValueSingle, [iptValueMin, iptValueMax], iptValueList only one work on page at same time
                iptValueMin: null,
                iptValueMax: null,
                iptValueList: [],
                relationType: item.relationType,
                relation: item.relation,
                backupName: item.name,
                backupRelationType: item.relationType,
                backupRelation: item.relation,
                backupValue: item.value,
                showTitle: true,
                showKeywordError: false
              }
              this.convertConditionStoredValue(condition)
              return condition
            })
          }
        }
      } finally {
        this.loading = false
      }
    },
    convertConditionStoredValue (condition) {
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.backupRelationType) {
        condition.iptValueList = condition.backupValue
      } else if (CONDITION_RELATION.BETWEEN === condition.backupRelation) {
        [condition.iptValueMin, condition.iptValueMax] = condition.backupValue
      } else {
        condition.iptValueSingle = condition.backupValue[0]
      }
    },
    validatorTagName (rule, value, callback) {
      if (typeof value === 'string' && !value.trim()) {
        callback(this.$t('admin.crm.message.tag.tagName'))
      }
      callback()
    },
    validateCondition (condition) {
      let result = false
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.relationType) {
        result = condition.iptValueList.length > 0
      } else if (CONDITION_RELATION.BETWEEN === condition.relation) {
        result = (condition.iptValueMin &&
        condition.iptValueMax &&
        !isNaN(condition.iptValueMin) &&
        !isNaN(condition.iptValueMax))
      } else {
        result = condition.iptValueSingle && !isNaN(condition.iptValueSingle)
      }
      condition.showKeywordError = !result

      return result
    },
    handleCancel () {
      this.$router.go(-1)
    },
    async handleSave (closeLoading) {
      const failed = _.some(this.tag.conditions, item => !this.validateCondition(item))
      if (failed) {
        return
      }

      const matchConditionRule = this.tag.condition_rule
      const tagDetail = {
        name: this.tag.tag_name.trim(),
        cn_name: this.tag.tag_cn_name.trim(),
        match_condition_rule: matchConditionRule
      }

      if (matchConditionRule === CONDITION_RULES.no) {
        tagDetail.category = 'manual'
        tagDetail.condition = JSON.stringify([])
      } else {
        tagDetail.category = 'smart'
        tagDetail.condition = JSON.stringify(_.map(this.tag.conditions, item => {
          return {
            name: item.name,
            relationType: item.relationType,
            relation: item.relation,
            value: this.generateConditionValue(item)
          }
        }))
      }

      let saveFunction = null
      if (this.isEditMode) {
        tagDetail.id = this.tag.id
        saveFunction = this.updateTag
      } else {
        saveFunction = this.createTag
      }
      const resp = await saveFunction(tagDetail)
      if (resp) {
        closeLoading()
        this.$router.push({ name: 'tagList' })
      } else {
        this.$message({ message: this.$t('admin.crm.message.tag.requestError'), type: 'error' })
      }
    },
    handleAddConditionBtnClick () {
      this.conditionVid += 1
      this.tag.conditions.push({
        _vid: this.conditionVid,
        name: CONDITIONS.SCHOOL.value,
        iptValue: null, // it is used for user enter each keyword in the inout box
        iptValueSingle: null, // iptValueSingle, [iptValueMin, iptValueMax], iptValueList only one work on page at same time
        iptValueMin: null,
        iptValueMax: null,
        iptValueList: [],
        relationType: CONDITIONS_RELATION_TYPE.CONTAINS,
        relation: CONDITION_RELATION.EQUALS,
        showTitle: false,
        showKeywordError: false
      })
      this.activeConditionItems.push(this.conditionVid)
    },
    removeConditionItem (index) {
      this.tag.conditions.splice(index, 1)
    },
    handleCollapseChange (activeNames) {
      _.map(this.tag.conditions, condition => {
        condition.showTitle = !activeNames.includes(condition._vid)
        // showTitle is true means that user wants to shrink the condition
        if (condition.showTitle && !this.validateCondition(condition)) {
          this.activeConditionItems.push(condition._vid)
          condition.showTitle = false
        }
      })
    },
    handleConditionChange (condition) {
      if (CONDITIONS_IN_RANGE.includes(condition.name)) {
        condition.relationType = this.conditionRelationType.RANGE
        condition.relation = CONDITION_RELATION.MORE
      } else {
        condition.relationType = this.conditionRelationType.CONTAINS
        condition.relation = CONDITION_RELATION.EQUALS
      }
      this.clearKeywordInput(condition)
      this.clearKeywordError(condition)
    },
    handleRangeTypeChange (condition) {
      this.clearKeywordInput(condition)
      this.clearKeywordError(condition)
    },
    handleInputEnter (condition) {
      if (condition.iptValue && condition.iptValue.trim()) {
        condition.iptValue = condition.iptValue.trim()
        condition.iptValueList.push(condition.iptValue)
      }
      condition.iptValue = ''
    },
    clearKeywordInput (condition) {
      condition.iptValueMin = null
      condition.iptValueMax = null
      condition.iptValueSingle = null
      condition.iptValueList = []
      if (this.isEditMode && condition.backupName === condition.name && condition.backupRelation === condition.relation) {
        this.convertConditionStoredValue(condition)
      }
    },
    clearKeywordError (condition) {
      condition.showKeywordError = false
    },
    removeConditionValueItem (condition, conditionValueItem) {
      condition.iptValueList.splice(condition.iptValueList.indexOf(conditionValueItem), 1)
    },
    generateConditionDisplayValue (condition) {
      let displayValue = condition.iptValueSingle
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.relationType) {
        const joinKey = this.$t('common.or')
        if (condition.name === 'gender') {
          displayValue = _.map(condition.iptValueList, item => {
            if (item === 'F') {
              return this.$t('common.gender.female')
            } else if (item === 'N') {
              return this.$t('common.gender.nonBinary')
            } else if (item === 'P') {
              return this.$t('common.gender.preferNotToDisclose')
            } else {
              return this.$t('common.gender.male')
            }
          }).join(joinKey)
        } else {
          displayValue = condition.iptValueList.join(joinKey)
        }
      } else if (CONDITION_RELATION.BETWEEN === condition.relation) {
        displayValue = `${condition.iptValueMin} - ${condition.iptValueMax}`
      }

      return displayValue
    },
    generateConditionValue (condition) {
      let value = condition.iptValueSingle
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.relationType) {
        value = condition.iptValueList
      } else if (CONDITION_RELATION.BETWEEN === condition.relation) {
        value = [condition.iptValueMin, condition.iptValueMax]
      } else {
        value = [condition.iptValueSingle]
      }

      return value
    },
    handleConfirmConditionBtnClick (condition) {
      if (!this.validateCondition(condition)) {
        return
      }

      condition.showTitle = true
      this.activeConditionItems.pop(condition._vid)
    },
    showEqualsCondition (conditionItem) {
      return CONDITIONS_IN_EQUALS.includes(conditionItem.name)
    },
    showContainsCondition (conditionItem) {
      return CONDITIONS_IN_CONTAINS.includes(conditionItem.name)
    }
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
.tag-editor {
  .condition-container {
    .add-condition-button {
      height: 32px;
      border: 1px dashed #c0c4cc;
      border-radius: 2px;
      text-align: center;
      color: #0096cc;
      font-size: 16px;
      line-height: 30px;
      cursor: pointer;
    }

    .condition-list {
      .el-collapse {
        border: unset;
      }

      .condition-item {
        border: 1px solid #DCDFE6;
        margin-top: 10px;

        .condition-item-title-wrapper {
          flex: 1;
          max-width: 100%;
          color: #606266;
          position: relative;

          .condition-item-title {
            display: flex;
            font-size: 14px;
            align-content: center;

            .item-name {
              margin-left: 24px;
              flex-shrink: 0;
            }

            .item-preview {
              flex: 1;
              margin-left: 5px;
              padding-right: 45px;
              overflow: hidden;
              text-overflow: ellipsis;
              word-break: keep-all;
              white-space: nowrap;
            }
          }

          .condition-operate {
            position: absolute;
            top: 0;
            right: 24px;
          }
        }

        .condition-content {
          padding: 16px 24px 26px;

          .condition-choice-container {
            .contains-container {
              .condition-value-container {
                border: 1px solid #DCDFE6;
                padding: 0 10px 0 10px;

                .condition-value-list {
                  margin-top: 10px;

                  .el-tag {
                    margin: 0 5px 5px 0;

                    ::v-deep {
                      .el-tag__close {
                        line-height: 18px;
                      }
                    }
                  }
                }

                .condition-value-item-input {
                  ::v-deep {
                    .el-input__inner {
                      border: none;
                      font-size: 12px;
                      padding: 0 10px;
                      background: #f9f9f9;
                    }
                  }
                }
              }
            }

            .range-container {
              .range-between {
                display: flex;
                align-items: center;

                .between-to {
                  margin: 0 10px 0 10px;
                }
              }
            }
          }

          .condition-content-item {
            margin-bottom: 15px;

            &.relation {
              margin-bottom: 8px;
            }

            ::v-deep .el-input {
              .el-input__inner {
                padding-left: 20px;
              }

              .el-input__icon {
                line-height: 1;
              }
            }
          }

          .keyword-error-message {
            font-size: 12px;
            margin-top: 4px;
            color: #F56C6C;
          }

          .keyword-error {
            border: 1px solid #F56C6C !important;
          }

          .confirm-condition-button {
            margin-top: 25px;
            text-align: right;

            .el-button {
              border-radius: 2px;
              padding: 5px 28px;
              line-height: 22px;
              font-weight: 500;
              font-size: 16px;
            }
          }
        }

        ::v-deep {
          .el-collapse-item__wrap {
            border: unset;
          }

          .el-collapse-item__arrow {
            display: none;
          }

          .el-collapse-item__content {
            padding-bottom: 0
          }

          .el-collapse-item__header {
            border: unset;
            line-height: 40px;
            height: 40px;
            background-color: #f4f4f4;
          }
        }
      }
    }
  }
}
</style>
